import { FormMethods } from '@if/mui'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'
import { FieldValues, Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CALENDAR_PREVIEW_FIELD } from './LoanCalendarActionPreview-schema'

export interface CalendarPreviewState {
  /** The recompute the agent is looking at, and the only thing Confirm can apply. */
  settled: SignedCalendarSimulation | null
  /** Whether the agent's own fields are valid. The preview field is not required, so this
   *  reports the fields alone and never waits on the preview it gates. */
  isValid: boolean
  /** Usable fields, no preview yet: the debounce or the request is still running. */
  recomputing: boolean
}

/**
 * Reads the preview gate off a form. Safe to call from a dialog's footer slots: the form
 * container re-renders them whenever its busy blockers change, and the recompute registers
 * one for exactly as long as the preview is out of step with the fields.
 */
export const calendarPreviewState = <TFieldValues extends FieldValues>(
  form: FormMethods<TFieldValues>,
): CalendarPreviewState => {
  const settled = (form.getValues(CALENDAR_PREVIEW_FIELD as Path<TFieldValues>) ??
    null) as SignedCalendarSimulation | null
  const { isValid } = form.formState
  return { settled, isValid, recomputing: isValid && settled === null }
}

/**
 * The footer slots every recomputing calendar dialog shares: Confirm spins while a preview
 * is owed, opens only once one has settled, and always says which of the two reasons — an
 * unfinished form, a recompute in flight, or someone else holding the loan — is keeping it
 * shut.
 */
export const useCalendarPreviewConfirm = (isLockedByOther: boolean) => {
  const { t } = useTranslation()

  return useMemo(
    () => ({
      confirmLoading: <TFieldValues extends FieldValues>({ form }: { form: FormMethods<TFieldValues> }) =>
        calendarPreviewState(form).recomputing,

      confirmDisabled: <TFieldValues extends FieldValues>({ form }: { form: FormMethods<TFieldValues> }) =>
        isLockedByOther || calendarPreviewState(form).settled === null,

      confirmTooltip: <TFieldValues extends FieldValues>({ form }: { form: FormMethods<TFieldValues> }) => {
        if (isLockedByOther) return t('common.lockTooltip.lockedByOther')
        const { settled, isValid } = calendarPreviewState(form)
        if (settled) return t('loan.payment.preview.apply')
        return isValid ? t('loan.payment.preview.awaitingCalendar') : t('loan.payment.preview.incompleteForm')
      },
    }),
    [isLockedByOther, t],
  )
}
